import { createContext, useCallback, useContext, useState } from 'react';
import {
  DropdownsParams,
  DropdownsContextData,
  DropdownsData,
  DropdownsProviderProps,
} from './types';
import { initialState } from './constants';
import { useAdditiveDropdown } from '@services/dropdowns/additive';
import { useBuildersDropdown } from '@services/dropdowns/builders';
import { useBuilderSuperintendentsDropdown } from '@services/dropdowns/builderSuperintendents';
import { useCompaniesDropdown } from '@services/dropdowns/companies';
import { useConcreteMixDropdown } from '@services/dropdowns/concreteMix';
import { useUsersDropdown } from '@services/dropdowns/users';
import { useInventoryItemsDropdown } from '@services/dropdowns/inventoryItems';
import { useLocationsDropdown } from '@services/dropdowns/locations';
import { usePourElementsDropdown } from '@services/dropdowns/pourElements';
import { usePourMethodsDropdown } from '@services/dropdowns/pourMethods';
import { useRoutesDropdown } from '@services/dropdowns/routes';
import { useSitesDropdown } from '@services/dropdowns/sites';
import { useStagesDropdown } from '@services/dropdowns/stages';
import { useSubtasksForFilesDropdown } from '@services/dropdowns/subtasksForFiles';
import { useTasksDropdown } from '@services/dropdowns/tasks';
import { usePanelZonesDropdown } from '@services/dropdowns/panel/zones';
import { usePanelRolesDropdown } from '@services/dropdowns/panel/roles';
import { usePanelStatusesDropdown } from '@services/dropdowns/panel/statuses';
import { useAppContext } from '@contexts/app';

const DropdownsContext = createContext<DropdownsContextData | Record<string, never>>({});

export const DropdownsProvider = ({ children }: DropdownsProviderProps) => {
  const [state, setState] = useState<DropdownsData>(initialState);
  const { params } = state;

  const { apiActivity } = useAppContext();

  const additive = useAdditiveDropdown(params.products);
  const builders = useBuildersDropdown();
  const builderSuperintendents = useBuilderSuperintendentsDropdown(params.builderSuperintendents);
  const companies = useCompaniesDropdown(params.companies);
  const concreteMix = useConcreteMixDropdown(params.products);
  const coordinators = useUsersDropdown({
    role: ['administrator', 'project_coordinator'],
  });
  const inventoryItems = useInventoryItemsDropdown(params.inventoryItems);
  const locations = useLocationsDropdown();
  const panel = {
    roles: usePanelRolesDropdown(),
    statuses: usePanelStatusesDropdown(),
    zones: usePanelZonesDropdown(),
  };
  const pourElements = usePourElementsDropdown();
  const pourMethods = usePourMethodsDropdown();
  const routes = useRoutesDropdown();
  const sites = useSitesDropdown();
  const stages = useStagesDropdown();
  const subtasksForFiles = useSubtasksForFilesDropdown(params.subtasksForFiles);
  const superintendents = useUsersDropdown({ role: ['superintendent'] });
  const tasks = useTasksDropdown(params.tasks);

  const setParams = useCallback((value: Partial<DropdownsParams>) => {
    setState((prev) => ({ ...prev, params: { ...prev.params, ...value } }));
  }, []);

  const setDisabled = (condition = false) => apiActivity || condition;

  return (
    <DropdownsContext.Provider
      value={{
        ...state,
        setParams,
        additive: {
          options: additive.data?.records || [],
          disabled: setDisabled(additive.isLoading || !additive.data?.records?.length),
        },
        boolean: {
          options: ['yes', 'no'] as const,
          disabled: setDisabled(),
        },
        builders: {
          options: builders.data?.records || [],
          disabled: setDisabled(builders.isLoading || !builders.data?.records?.length),
        },
        builderSuperintendents: {
          options: builderSuperintendents.data?.records || [],
          disabled: setDisabled(
            builderSuperintendents.isLoading || !builderSuperintendents.data?.records?.length,
          ),
        },
        companies: {
          options: {
            companies: companies.data?.companies || [],
            jobContacts: companies.data?.jobContacts || [],
          },
          disabled: setDisabled(
            companies.isLoading ||
              (!companies.data?.companies?.length && !companies.data?.jobContacts?.length),
          ),
        },
        concreteMix: {
          options: concreteMix.data?.records || [],
          disabled: setDisabled(concreteMix.isLoading || !concreteMix.data?.records?.length),
        },
        constructionTypes: {
          options: ['residential', 'commercial'] as const,
          disabled: setDisabled(),
        },
        coordinators: {
          options: coordinators.data?.records || [],
          disabled: setDisabled(coordinators.isLoading || !coordinators.data?.records?.length),
        },
        inventoryItems: {
          options: inventoryItems.data?.records || [],
          disabled: setDisabled(inventoryItems.isLoading || !inventoryItems.data?.records?.length),
        },
        locations: {
          options: locations.data?.records || [],
          disabled: setDisabled(locations.isLoading || !locations.data?.records?.length),
        },
        panel: {
          roles: {
            options: panel.roles.data?.records || [],
            disabled: setDisabled(panel.roles.isLoading || !panel.roles.data?.records?.length),
          },
          statuses: {
            options: panel.statuses.data?.records || [],
            disabled: setDisabled(
              panel.statuses.isLoading || !panel.statuses.data?.records?.length,
            ),
          },
          zones: {
            options: panel.zones.data?.records || [],
            disabled: setDisabled(panel.zones.isLoading || !panel.zones.data?.records?.length),
          },
        },
        pourElements: {
          options: pourElements.data?.records || [],
          disabled: setDisabled(pourElements.isLoading || !pourElements.data?.records?.length),
        },
        pourMethods: {
          options: pourMethods.data?.records || [],
          disabled: setDisabled(pourMethods.isLoading || !pourMethods.data?.records?.length),
        },
        rateTypes: {
          options: ['SPACING_IN_MINUTES', 'QTY_PER_HOUR'] as const,
          disabled: setDisabled(),
        },
        routes: {
          options: routes.data?.records || [],
          disabled: setDisabled(routes.isLoading || !routes.data?.records?.length),
        },
        sites: {
          options: sites.data?.records || [],
          disabled: setDisabled(sites.isLoading || !sites.data?.records?.length),
        },
        stages: {
          options: stages.data?.records || [],
          disabled: setDisabled(stages.isLoading || !stages.data?.records?.length),
        },
        states: {
          options: ['construction', 'pre_construction', 'follow_up'] as const,
          disabled: setDisabled(apiActivity),
        },
        statuses: {
          options: ['pending', 'approved', 'rejected'] as const,
          disabled: setDisabled(apiActivity),
        },
        subtasksForFiles: {
          options: subtasksForFiles.data?.records || [],
          disabled: setDisabled(
            subtasksForFiles.isLoading || !subtasksForFiles.data?.records?.length,
          ),
        },
        superintendents: {
          options: superintendents.data?.records || [],
          disabled: setDisabled(coordinators.isLoading || !coordinators.data?.records?.length),
        },
        tasks: {
          options: tasks.data?.records || [],
          disabled: setDisabled(tasks.isLoading || !tasks.data?.records?.length),
        },
        workloadTypes: {
          options: ['month', 'two-week', 'week', 'day'] as const,
          disabled: setDisabled(),
        },
      }}>
      {children}
    </DropdownsContext.Provider>
  );
};

// eslint-disable-next-line react-refresh/only-export-components
export const useDropdownsContext = () => useContext(DropdownsContext);
